import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { clients, clientStats } from '../../api/client'
import PageHeader from '../../components/PageHeader'

function formatDate(dateString) {
  if (!dateString) return ''
  const date = new Date(dateString)
  return date.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })
}

function formatShortDate(dateString) {
  if (!dateString) return ''
  const date = new Date(dateString)
  return date.toLocaleDateString([], { month: 'short', day: 'numeric' })
}

// Simple SVG line chart for progress visualization
function ProgressChart({ data, dataKey, label, color, unit }) {
  if (!data || data.length < 2) {
    return (
      <div className="bg-white rounded-xl p-4 shadow-sm">
        <h3 className="text-sm font-medium text-gray-500 mb-2">{label} Trend</h3>
        <p className="text-gray-400 text-sm text-center py-8">Need at least 2 entries to show trend</p>
      </div>
    )
  }

  // Filter data to only entries with the dataKey and reverse for chronological order
  const chartData = data
    .filter(d => d[dataKey] != null)
    .slice(0, 10)
    .reverse()

  if (chartData.length < 2) {
    return (
      <div className="bg-white rounded-xl p-4 shadow-sm">
        <h3 className="text-sm font-medium text-gray-500 mb-2">{label} Trend</h3>
        <p className="text-gray-400 text-sm text-center py-8">Need at least 2 entries to show trend</p>
      </div>
    )
  }

  const values = chartData.map(d => d[dataKey])
  const minVal = Math.min(...values)
  const maxVal = Math.max(...values)
  const range = maxVal - minVal || 1
  const padding = range * 0.1

  const width = 280
  const height = 120
  const chartPadding = { top: 10, right: 10, bottom: 25, left: 40 }
  const chartWidth = width - chartPadding.left - chartPadding.right
  const chartHeight = height - chartPadding.top - chartPadding.bottom

  const points = chartData.map((d, i) => ({
    x: chartPadding.left + (i / (chartData.length - 1)) * chartWidth,
    y: chartPadding.top + chartHeight - ((d[dataKey] - minVal + padding) / (range + 2 * padding)) * chartHeight,
    value: d[dataKey],
    date: d.recordedDate || d.recordedAt,
  }))

  const pathD = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ')

  // Calculate change
  const firstVal = chartData[0][dataKey]
  const lastVal = chartData[chartData.length - 1][dataKey]
  const change = lastVal - firstVal
  const changePercent = ((change / firstVal) * 100).toFixed(1)

  return (
    <div className="bg-white rounded-xl p-4 shadow-sm">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-medium text-gray-500">{label} Trend</h3>
        <span className={`text-xs font-medium ${change < 0 ? 'text-green-600' : change > 0 ? 'text-red-600' : 'text-gray-500'}`}>
          {change > 0 ? '+' : ''}{change.toFixed(1)} {unit} ({changePercent}%)
        </span>
      </div>
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full">
        {/* Grid lines */}
        <line x1={chartPadding.left} y1={chartPadding.top} x2={chartPadding.left} y2={height - chartPadding.bottom} stroke="#e5e7eb" strokeWidth="1" />
        <line x1={chartPadding.left} y1={height - chartPadding.bottom} x2={width - chartPadding.right} y2={height - chartPadding.bottom} stroke="#e5e7eb" strokeWidth="1" />

        {/* Y-axis labels */}
        <text x={chartPadding.left - 5} y={chartPadding.top + 5} textAnchor="end" fontSize="8" fill="#9ca3af">{maxVal.toFixed(1)}</text>
        <text x={chartPadding.left - 5} y={height - chartPadding.bottom} textAnchor="end" fontSize="8" fill="#9ca3af">{minVal.toFixed(1)}</text>

        {/* Line */}
        <path d={pathD} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />

        {/* Points */}
        {points.map((p, i) => (
          <circle key={i} cx={p.x} cy={p.y} r="4" fill={color} />
        ))}

        {/* X-axis labels (first and last) */}
        <text x={points[0].x} y={height - 5} textAnchor="start" fontSize="8" fill="#9ca3af">{formatShortDate(points[0].date)}</text>
        <text x={points[points.length - 1].x} y={height - 5} textAnchor="end" fontSize="8" fill="#9ca3af">{formatShortDate(points[points.length - 1].date)}</text>
      </svg>
    </div>
  )
}

function StatCard({ label, value, unit, trend }) {
  return (
    <div className="bg-white rounded-xl p-4 shadow-sm">
      <p className="text-sm text-gray-500 mb-1">{label}</p>
      <div className="flex items-baseline gap-1">
        <span className="text-2xl font-bold text-gray-900">{value ?? '-'}</span>
        {unit && <span className="text-sm text-gray-500">{unit}</span>}
      </div>
      {trend !== undefined && trend !== null && (
        <p className={`text-xs mt-1 ${trend > 0 ? 'text-red-500' : trend < 0 ? 'text-green-500' : 'text-gray-500'}`}>
          {trend > 0 ? '+' : ''}{trend} from last
        </p>
      )}
    </div>
  )
}

function AddStatsModal({ isOpen, onClose, onSubmit, clientName }) {
  const [formData, setFormData] = useState({
    weightKg: '',
    bodyFatPercent: '',
    waistCm: '',
    chestCm: '',
    hipsCm: '',
    notes: '',
  })
  const [isSubmitting, setIsSubmitting] = useState(false)

  async function handleSubmit(e) {
    e.preventDefault()
    setIsSubmitting(true)

    const data = {}
    if (formData.weightKg) data.weightKg = parseFloat(formData.weightKg)
    if (formData.bodyFatPercent) data.bodyFatPercent = parseFloat(formData.bodyFatPercent)
    if (formData.waistCm) data.waistCm = parseFloat(formData.waistCm)
    if (formData.chestCm) data.chestCm = parseFloat(formData.chestCm)
    if (formData.hipsCm) data.hipsCm = parseFloat(formData.hipsCm)
    if (formData.notes) data.notes = formData.notes
    data.recordedDate = new Date().toISOString()

    await onSubmit(data)
    setFormData({ weightKg: '', bodyFatPercent: '', waistCm: '', chestCm: '', hipsCm: '', notes: '' })
    setIsSubmitting(false)
    onClose()
  }

  if (!isOpen) return null

  const hasAnyMeasurement = formData.weightKg || formData.bodyFatPercent || formData.waistCm || formData.chestCm || formData.hipsCm

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white w-full max-w-lg rounded-xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold">Record Stats</h2>
            {clientName && <p className="text-sm text-gray-500">for {clientName}</p>}
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Weight (kg)
              </label>
              <input
                type="number"
                step="0.1"
                value={formData.weightKg}
                onChange={(e) => setFormData(prev => ({ ...prev, weightKg: e.target.value }))}
                placeholder="e.g., 75.5"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Body Fat (%)
              </label>
              <input
                type="number"
                step="0.1"
                value={formData.bodyFatPercent}
                onChange={(e) => setFormData(prev => ({ ...prev, bodyFatPercent: e.target.value }))}
                placeholder="e.g., 15.0"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Chest (cm)
              </label>
              <input
                type="number"
                step="0.1"
                value={formData.chestCm}
                onChange={(e) => setFormData(prev => ({ ...prev, chestCm: e.target.value }))}
                placeholder="e.g., 100"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Waist (cm)
              </label>
              <input
                type="number"
                step="0.1"
                value={formData.waistCm}
                onChange={(e) => setFormData(prev => ({ ...prev, waistCm: e.target.value }))}
                placeholder="e.g., 80"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Hips (cm)
              </label>
              <input
                type="number"
                step="0.1"
                value={formData.hipsCm}
                onChange={(e) => setFormData(prev => ({ ...prev, hipsCm: e.target.value }))}
                placeholder="e.g., 95"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              placeholder="Any additional notes..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none resize-none"
            />
          </div>

          <button
            type="submit"
            disabled={isSubmitting || !hasAnyMeasurement}
            className="w-full bg-primary-600 text-white py-3 rounded-lg font-medium hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isSubmitting ? 'Saving...' : 'Save Stats'}
          </button>
        </form>
      </div>
    </div>
  )
}

export default function ClientStatsPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [showAddModal, setShowAddModal] = useState(false)
  const [deletingId, setDeletingId] = useState(null)

  const { data: client } = useQuery({
    queryKey: ['client', id],
    queryFn: () => clients.get(id),
  })

  const { data: statsList, isLoading } = useQuery({
    queryKey: ['clientStats', id],
    queryFn: () => clientStats.list(id),
  })

  const addStatsMutation = useMutation({
    mutationFn: (data) => clientStats.create(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clientStats', id] })
    },
  })

  const deleteStatsMutation = useMutation({
    mutationFn: (statsId) => clientStats.delete(statsId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clientStats', id] })
      setDeletingId(null)
    },
  })

  function handleDelete(statsId) {
    if (deletingId === statsId) {
      deleteStatsMutation.mutate(statsId)
    } else {
      setDeletingId(statsId)
    }
  }

  const latestStats = statsList?.[0]
  const previousStats = statsList?.[1]

  const weightTrend = latestStats?.weightKg && previousStats?.weightKg
    ? (latestStats.weightKg - previousStats.weightKg).toFixed(1)
    : null

  const bodyFatTrend = latestStats?.bodyFatPercent && previousStats?.bodyFatPercent
    ? (latestStats.bodyFatPercent - previousStats.bodyFatPercent).toFixed(1)
    : null

  const clientName = client ? `${client.firstName} ${client.lastName}` : ''

  return (
    <div className="min-h-screen bg-gray-100">
      <PageHeader
        title={client ? `${client.firstName}'s Stats` : 'Stats'}
        showBack
        backTo={`/clients/${id}`}
        actions={
          <button
            onClick={() => setShowAddModal(true)}
            className="p-2 text-primary-600 hover:text-primary-700"
            title="Add stats"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
          </button>
        }
      />

      <div className="px-4 py-4 space-y-4">
        {/* Current Stats */}
        <div className="grid grid-cols-2 gap-4">
          <StatCard
            label="Weight"
            value={latestStats?.weightKg}
            unit="kg"
            trend={weightTrend}
          />
          <StatCard
            label="Body Fat"
            value={latestStats?.bodyFatPercent}
            unit="%"
            trend={bodyFatTrend}
          />
        </div>

        {/* Additional measurements if available */}
        {latestStats && (latestStats.waistCm || latestStats.chestCm || latestStats.hipsCm) && (
          <div className="grid grid-cols-3 gap-3">
            {latestStats.chestCm && (
              <div className="bg-white rounded-xl p-3 shadow-sm text-center">
                <p className="text-xs text-gray-500">Chest</p>
                <p className="font-semibold text-gray-900">{latestStats.chestCm} cm</p>
              </div>
            )}
            {latestStats.waistCm && (
              <div className="bg-white rounded-xl p-3 shadow-sm text-center">
                <p className="text-xs text-gray-500">Waist</p>
                <p className="font-semibold text-gray-900">{latestStats.waistCm} cm</p>
              </div>
            )}
            {latestStats.hipsCm && (
              <div className="bg-white rounded-xl p-3 shadow-sm text-center">
                <p className="text-xs text-gray-500">Hips</p>
                <p className="font-semibold text-gray-900">{latestStats.hipsCm} cm</p>
              </div>
            )}
          </div>
        )}

        {/* Progress Charts */}
        <ProgressChart
          data={statsList}
          dataKey="weightKg"
          label="Weight"
          color="#3B82F6"
          unit="kg"
        />
        <ProgressChart
          data={statsList}
          dataKey="bodyFatPercent"
          label="Body Fat"
          color="#10B981"
          unit="%"
        />
        <ProgressChart
          data={statsList}
          dataKey="waistCm"
          label="Waist"
          color="#F59E0B"
          unit="cm"
        />
        <ProgressChart
          data={statsList}
          dataKey="chestCm"
          label="Chest"
          color="#8B5CF6"
          unit="cm"
        />
        <ProgressChart
          data={statsList}
          dataKey="hipsCm"
          label="Hips"
          color="#EC4899"
          unit="cm"
        />

        {/* History */}
        <div className="bg-white rounded-xl shadow-sm">
          <div className="px-4 py-3 border-b border-gray-100">
            <h2 className="font-semibold text-gray-900">History</h2>
          </div>

          {isLoading ? (
            <div className="p-4 space-y-3">
              {[1, 2, 3].map(i => (
                <div key={i} className="animate-pulse flex items-center justify-between">
                  <div className="h-4 bg-gray-200 rounded w-24"></div>
                  <div className="h-4 bg-gray-200 rounded w-16"></div>
                </div>
              ))}
            </div>
          ) : statsList?.length === 0 ? (
            <div className="p-8 text-center">
              <p className="text-gray-500">No stats recorded yet</p>
              <button
                onClick={() => setShowAddModal(true)}
                className="mt-2 text-primary-600 font-medium"
              >
                Add first entry
              </button>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {statsList?.map((stat) => (
                <div key={stat.id} className="px-4 py-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">{formatDate(stat.recordedDate || stat.recordedAt)}</span>
                    <div className="flex items-center gap-3 text-sm">
                      {stat.weightKg && (
                        <span className="text-gray-900">{stat.weightKg} kg</span>
                      )}
                      {stat.bodyFatPercent && (
                        <span className="text-gray-600">{stat.bodyFatPercent}%</span>
                      )}
                      <button
                        onClick={() => handleDelete(stat.id)}
                        onBlur={() => { if (deletingId === stat.id) setTimeout(() => setDeletingId(null), 200) }}
                        className={`p-1 rounded transition-colors ${
                          deletingId === stat.id
                            ? 'text-red-600 bg-red-50 hover:bg-red-100'
                            : 'text-gray-300 hover:text-red-500'
                        }`}
                        title={deletingId === stat.id ? 'Click again to confirm' : 'Delete entry'}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                  {(stat.chestCm || stat.waistCm || stat.hipsCm) && (
                    <div className="flex items-center gap-3 mt-1 text-xs text-gray-400">
                      {stat.chestCm && <span>Chest: {stat.chestCm}cm</span>}
                      {stat.waistCm && <span>Waist: {stat.waistCm}cm</span>}
                      {stat.hipsCm && <span>Hips: {stat.hipsCm}cm</span>}
                    </div>
                  )}
                  {stat.notes && (
                    <p className="text-xs text-gray-500 mt-1">{stat.notes}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <AddStatsModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSubmit={addStatsMutation.mutateAsync}
        clientName={clientName}
      />
    </div>
  )
}
